import { apiClient } from './apiClient';

export interface ClientListItem {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  is_system_tenant?: boolean;
  auth_mode: string;
  storage_backend: string;
  extraction_policy: string;
  created_at: string;
}

export interface ClientDetail {
  id: string;
  name: string;
  slug: string;
  external_id: string | null;
  auth_mode: string;
  storage_backend: string;
  storage_config: Record<string, any> | null;
  extraction_policy: string;
  extraction_schedule_cron: string | null;
  ingestion_modes_enabled: Record<string, boolean>;
  s3_polling_prefix: string | null;
  s3_polling_policy: 'interval' | 'scheduled';
  s3_polling_interval_minutes: number | null;
  s3_polling_cron: string | null;
  last_polled_at: string | null;
  is_active: boolean;
  is_system_tenant?: boolean;
  created_at: string;
}

export interface ClientCreatePayload {
  name: string;
  slug: string;
  storage_backend?: string;
  external_id?: string | null;
}

export interface ClientIdentityPatch {
  name?: string;
  slug?: string;
  auth_mode?: string;
  is_active?: boolean;
}

export interface ClientStoragePatch {
  storage_backend?: string;
  storage_config?: Record<string, any> | null;
}

export interface ClientIngestionPatch {
  ingestion_modes_enabled?: Record<string, boolean>;
  s3_polling_prefix?: string | null;
  s3_polling_policy?: 'interval' | 'scheduled';
  s3_polling_interval_minutes?: number | null;
  s3_polling_cron?: string | null;
}

export interface ClientExtractionPolicyPatch {
  extraction_policy?: 'auto' | 'manual' | 'scheduled';
  extraction_schedule_cron?: string | null;
}

export interface ClientUpdatePayload {
  name?: string;
  external_id?: string | null;
  auth_mode?: string;
  storage_backend?: string;
  storage_config?: Record<string, any> | null;
  extraction_policy?: string;
  extraction_schedule_cron?: string | null;
  ingestion_modes_enabled?: Record<string, boolean>;
  s3_polling_prefix?: string | null;
  s3_polling_policy?: 'interval' | 'scheduled';
  s3_polling_interval_minutes?: number | null;
  s3_polling_cron?: string | null;
  is_active?: boolean;
}

export interface StorageTestResult {
  ok: boolean;
  error?: string | null;
}

export interface ClientDomain {
  domain_code: string;
  is_active: boolean;
}

export interface ClientActiveConfig {
  domain_code: string;
  prompt_id: string;
  extraction_schema_id: string;
  ocr_config_id: string;
  llm_config_id: string;
  updated_at?: string | null;
  updated_by_user_id?: string | null;
}

export interface ClientActiveConfigUpdate {
  prompt_id?: string;
  extraction_schema_id?: string;
  ocr_config_id?: string;
  llm_config_id?: string;
}

export interface ClientUser {
  user_id: string;
  username: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
}

const BASE = '/admin/clients';

export const adminClientsService = {
  async listClients(options?: { includeSystem?: boolean }): Promise<ClientListItem[]> {
    // PROD1.5.6 #558 — `includeSystem=true` ajoute le tenant systeme à la liste.
    const qs = options?.includeSystem ? '?include_system=true' : '';
    const res = await apiClient.get<ClientListItem[]>(`${BASE}${qs}`);
    return res.data;
  },

  async getClient(id: string): Promise<ClientDetail> {
    const res = await apiClient.get<ClientDetail>(`${BASE}/${id}`);
    return res.data;
  },

  async createClient(payload: ClientCreatePayload): Promise<ClientDetail> {
    const res = await apiClient.post<ClientDetail>(BASE, payload);
    return res.data;
  },

  async updateClient(id: string, payload: ClientUpdatePayload): Promise<ClientDetail> {
    const res = await apiClient.put<ClientDetail>(`${BASE}/${id}`, payload);
    return res.data;
  },

  async patchIdentity(id: string, payload: ClientIdentityPatch): Promise<ClientDetail> {
    const res = await apiClient.patch<ClientDetail>(`${BASE}/${id}/identity`, payload);
    return res.data;
  },

  async patchStorage(id: string, payload: ClientStoragePatch): Promise<ClientDetail> {
    const res = await apiClient.patch<ClientDetail>(`${BASE}/${id}/storage`, payload);
    return res.data;
  },

  async patchIngestion(id: string, payload: ClientIngestionPatch): Promise<ClientDetail> {
    const res = await apiClient.patch<ClientDetail>(`${BASE}/${id}/ingestion`, payload);
    return res.data;
  },

  async patchExtractionPolicy(
    id: string,
    payload: ClientExtractionPolicyPatch,
  ): Promise<ClientDetail> {
    const res = await apiClient.patch<ClientDetail>(`${BASE}/${id}/extraction-policy`, payload);
    return res.data;
  },

  async softDeleteClient(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async hardDeleteClient(id: string): Promise<void> {
    // PROD1.5.5 — suppression physique (tenant inactif ET vide uniquement)
    await apiClient.delete(`${BASE}/${id}/permanent`);
  },

  async testStorage(id: string): Promise<StorageTestResult> {
    const res = await apiClient.post<StorageTestResult>(`${BASE}/${id}/test-storage`);
    return res.data;
  },

  async getStorageStats(id: string): Promise<{
    documents_count: number;
    ground_truth_documents_count: number;
    total_stored_objects: number;
  }> {
    const res = await apiClient.get<{
      documents_count: number;
      ground_truth_documents_count: number;
      total_stored_objects: number;
    }>(`${BASE}/${id}/storage-stats`);
    return res.data;
  },

  async listDomains(id: string): Promise<ClientDomain[]> {
    const res = await apiClient.get<ClientDomain[]>(`${BASE}/${id}/domains`);
    return res.data;
  },

  async setDomains(id: string, domainCodes: string[]): Promise<ClientDomain[]> {
    const res = await apiClient.put<ClientDomain[]>(`${BASE}/${id}/domains`, {
      domain_codes: domainCodes,
    });
    return res.data;
  },

  async listActiveConfigs(id: string): Promise<ClientActiveConfig[]> {
    const res = await apiClient.get<ClientActiveConfig[]>(`${BASE}/${id}/active-configs`);
    return res.data;
  },

  async setActiveConfig(
    id: string,
    domainCode: string,
    payload: ClientActiveConfigUpdate
  ): Promise<ClientActiveConfig> {
    const res = await apiClient.put<ClientActiveConfig>(
      `${BASE}/${id}/active-configs/${domainCode}`,
      payload
    );
    return res.data;
  },

  async listUsers(id: string): Promise<ClientUser[]> {
    const res = await apiClient.get<ClientUser[]>(`${BASE}/${id}/users`);
    return res.data;
  },

  async searchUsers(): Promise<Array<{ id: string; email: string; username: string; full_name?: string }>> {
    const res = await apiClient.get<Array<{ id: string; email: string; username: string; full_name?: string }>>(
      `/admin/users/`,
    );
    return res.data;
  },

  async addUserExisting(id: string, userId: string, role: string): Promise<ClientUser> {
    const res = await apiClient.post<ClientUser>(`${BASE}/${id}/users`, {
      user_id: userId,
      role,
    });
    return res.data;
  },

  async updateUserRole(id: string, userId: string, role: string): Promise<ClientUser> {
    const res = await apiClient.put<ClientUser>(`${BASE}/${id}/users/${userId}`, { role });
    return res.data;
  },

  async removeUser(id: string, userId: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}/users/${userId}`);
  },

  async listUserClients(userId: string): Promise<UserClientAssignment[]> {
    const res = await apiClient.get<UserClientAssignment[]>(`/admin/users/${userId}/clients`);
    return res.data;
  },
};

export interface UserClientAssignment {
  client_id: string;
  client_name: string;
  client_slug: string;
  role: string;
}
